import { App, Card } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import { type Dayjs } from "dayjs"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useFocusTableRow } from "../../../../../../hooks/use-focus-table-row.ts"

import { useDateFormat } from "../../../../../../hooks/use-date-format.ts"
import { useNotifications } from "../../../../../../hooks/use-notifications.ts"
import {
  nextPageUrl,
  SCROLL_PAGE_SIZE
} from "../../../../../../redux/shared/paged-list.ts"
import { type RootState } from "../../../../../../store.ts"
import { DATE_FORMAT } from "../../../../../../utils/date/date.ts"
import { extractParamsFromNextUrl } from "../../../../../../utils/form/extract-params-from-next-url.ts"
import { budgetFormat } from "../../../../../../utils/number/budget-format.ts"
import { serializeData } from "../../../../../../utils/utils"
import {
  canEditProject,
  isArchivedProject
} from "../../../../helpers/project-edit-visibility.ts"
import { useProjectOutletContext } from "../../../../hooks/use-project-outlet-context.tsx"
import { type MilestoneDependencyWriteData } from "../../../../api/project-milestone-dependencies.services.ts"
import {
  calculateEffectiveSchedule,
  DEFAULT_MILESTONE_DURATION_DAYS,
  diffEffectiveSchedule,
  type EffectiveScheduleEntry
} from "../../../utils/milestone-effective-schedule.ts"
import {
  MILESTONE_SCHEDULE_FIELDS,
  type MilestoneScheduleField,
  type MilestoneScheduleFields,
  milestoneScheduleFieldErrors,
  resolveMilestoneSchedule
} from "../../../utils/milestone-effort.ts"
import { type MilestoneSaveResult } from "./ProjectViewMilestones/use-milestone-schedule-fields.ts"
import {
  fetchFinancialAlerts,
  fetchFinancialSnapshot,
  fetchFinancialTransactionItemsFilterOptions
} from "../../../../redux/project-finance/project-finance.thunks.ts"
import {
  createMilestoneDependency,
  deleteMilestoneDependency,
  fetchProjectMilestoneDependencies,
  patchMilestoneDependency
} from "../../../../redux/project-milestones/project-milestone-dependencies.thunks.ts"
import {
  bulkDeleteProjectMilestones,
  bulkPatchProjectMilestones,
  createProjectMilestone,
  deleteProjectMilestone,
  OMIT_FINANCIAL_ITEMS,
  fetchProjectMilestoneById,
  fetchProjectMilestones,
  fetchProjectMilestonesForBulk,
  fetchProjectMilestonesFilterOptions,
  patchProjectMilestone,
  reorderProjectMilestones
} from "../../../../redux/project-milestones/project-milestones.thunks.ts"
import { projectSetProp } from "../../../../redux/project.slice.ts"
import {
  addProjectTypeToProject,
  recordProjectTypeContractDecision,
  removeProjectTypeToProject,
  saveProjectTypeAsTemplate,
  updateProjectTypeToProject,
  reorderProjectTypes
} from "../../../../redux/project.thunks.ts"
import type { ProjectMilestoneFilters } from "../../../../redux/project.types.ts"
import type { MilestoneDependency } from "../../../../types/project-milestone-dependency.types.ts"
import type {
  ProjectMilestone,
  ProjectMilestoneFinanceItem
} from "../../../../types/project-phases.types.tsx"
import {
  type ProjectTypeDetail,
  type ProjectTypeFormData
} from "../../../../types/project.types.ts"
import { type ContractDecision } from "../../../../api/services.types.ts"
import {
  deleteNeedsContractPrompt,
  projectTypeContractPrompt
} from "../../../../helpers/project-type-contract-prompt.ts"
import { MILESTONE_STATUSES } from "../../../constants/milestone-statuses.ts"
import { PROJECT_ENTITY_CATEGORIES } from "../../../constants/project-entity-category.ts"
import {
  FINANCE_ITEM_TYPES,
  type FinanceItemTypes
} from "../../../constants/finance-item-constants.ts"
import { ContractDecisionModal } from "./ContractDecisionModal/ContractDecisionModal.tsx"
import { DeleteProjectTypeModal } from "./ContractDecisionModal/DeleteProjectTypeModal.tsx"
import { MilestoneDependenciesModal } from "./ProjectViewMilestones/MilestoneDependenciesModal.tsx"
import { ProjectMilestoneModal } from "./ProjectViewMilestones/ProjectMilestoneModal.tsx"
import { ProjectViewMilestonesGantt } from "./ProjectViewMilestones/ProjectViewMilestonesGantt.tsx"
import { ProjectViewMilestonesHeader } from "./ProjectViewMilestones/ProjectViewMilestonesHeader/ProjectViewMilestonesHeader.tsx"
import { type MilestoneBulkActionParams } from "./ProjectViewMilestones/MilestonesBulkActionBar.tsx"
import { milestoneDatePatches } from "./ProjectViewMilestones/milestone-bulk-dates.ts"
import { type MilestoneBulkResult } from "../../../../redux/project-milestones/project-milestones.types.ts"
import { ProjectViewMilestonesList } from "./ProjectViewMilestones/ProjectViewMilestonesList.tsx"
import {
  defaultScheduleImpactChoice,
  idsToSave,
  MilestoneScheduleImpactContent,
  type ScheduleImpactEntry
} from "./ProjectViewMilestones/MilestoneScheduleImpactContent.tsx"
import { SaveProjectTypeAsTemplateModal } from "./SaveProjectTypeAsTemplateModal.tsx"
import {
  confirmRepeatedType,
  instanceKeyOf,
  isRepeatedTypeRefusal,
  newestInstanceOf,
  typeLabel
} from "../../../../helpers/repeated-project-type.ts"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"

type MilestonesView = "table" | "gantt"
type EffectiveSchedule = Map<string, EffectiveScheduleEntry>
type ScheduleImpact = ReturnType<typeof diffEffectiveSchedule>

// "Load all" asks for the whole list in one request, up to this many rows; a
// longer list goes on scrolling from there.
export const MILESTONES_LOAD_ALL_CAP = 500

const shiftedOnly = (impact: ScheduleImpact, after: EffectiveSchedule) =>
  impact.filter((entry) => !after.get(entry.id)?.isGenerated)

// A per-browser UI preference, not project data — deliberately not project-
// scoped and not part of any redux slice, so it survives switching between
// project-type tabs (which never touches it) and between projects entirely
// (the "remember how I prefer to view this" behavior the user asked for).
const MILESTONES_VIEW_STORAGE_KEY = "ezbuild:milestones-view"

const readStoredMilestonesView = (): MilestonesView => {
  try {
    const stored = localStorage.getItem(MILESTONES_VIEW_STORAGE_KEY)
    return stored === "gantt" ? "gantt" : "table"
  } catch {
    return "table"
  }
}

const writeStoredMilestonesView = (view: MilestonesView) => {
  try {
    localStorage.setItem(MILESTONES_VIEW_STORAGE_KEY, view)
  } catch {
    // Best-effort only — a private window or blocked storage just means the
    // preference won't be remembered next time, not a broken feature.
  }
}

export const ProjectViewMilestonesSection = ({
  userId,
  projectPermissions,
  saveAsTemplateAllowed,
  pages,
  data,
  fetching,
  filters,
  filterOptions,
  dependencies,
  createMilestone,
  updateMilestone,
  removeMilestone,
  reorderMilestones,
  bulkPatchMilestones,
  bulkDeleteMilestones,
  getMilestonesForBulk,
  getFilterOptions,
  getFinanceFilterOptions,
  getProjectMilestones,
  getProjectMilestoneById,
  getMilestoneDependencies,
  createDependency,
  updateDependency,
  removeDependency,
  addProjectType,
  // updateProjectType,
  removeProjectType,
  updateProjectType,
  recordContractDecision,
  saveProjectTypeAsTemplate,
  reorderProjectTypes,
  setProp,
  refetchFinancialSnapshot,
  getFinancialAlerts
}: ConnectedProps<typeof connector>) => {
  // A notification's "Review milestone" names the exact milestone; this
  // is what brings that row into view once the list has loaded it.
  useFocusTableRow("milestone")

  const { t } = useTranslation()
  const { normalizeDate, parseDate, userFormat } = useDateFormat()
  const formatDay = useCallback(
    (day: Dayjs) => day.format(userFormat || DATE_FORMAT),
    [userFormat]
  )
  const { modal, notification } = App.useApp()
  const notifications = useNotifications()
  const { project } = useProjectOutletContext()

  const id = project?.id
  const count = data?.count || 0
  const milestones = useMemo(() => data?.results || [], [data?.results])
  const showFinanceItems =
    !!projectPermissions?.planned_finances?.project_planned_finance_view

  const [editableRow, setEditableRow] = useState<string>("")
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [showAddMilestone, setShowAddMilestone] = useState(false)
  const [pendingDecision, setPendingDecision] =
    useState<ProjectTypeDetail | null>(null)
  const [pendingDeletion, setPendingDeletion] =
    useState<ProjectTypeDetail | null>(null)
  const [savingAsTemplate, setSavingAsTemplate] =
    useState<ProjectTypeDetail | null>(null)
  const [pendingReversal, setPendingReversal] = useState<{
    previous: ProjectTypeDetail
    next: ProjectTypeFormData
  } | null>(null)
  const [financeOptions, setFinanceOptions] = useState<
    Record<string, BaseOptionType[]>
  >({})
  const [manageDependenciesFor, setManageDependenciesFor] =
    useState<ProjectMilestone | null>(null)
  const [savingDependency, setSavingDependency] = useState(false)
  const [view, setViewState] = useState<MilestonesView>(
    readStoredMilestonesView
  )
  const setView = useCallback((next: MilestonesView) => {
    setViewState(next)
    writeStoredMilestonesView(next)
  }, [])

  const setFilters = useCallback(
    (data: Partial<ProjectMilestoneFilters>) => {
      setProp({
        key: "milestonesFilters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const handleRefetchFinancialSnapshot = useCallback(() => {
    refetchFinancialSnapshot(id)
    // A milestone edit changes the totals the alert rules are evaluated
    // against — without this, a resolved (or newly triggered) condition
    // stays stale until something else happens to refetch alerts.
    void getFinancialAlerts(id)
  }, [refetchFinancialSnapshot, getFinancialAlerts, id])

  const handleGetMilestones = useCallback(async () => {
    const serializeFilters = serializeData(
      filters || {}
    ) as ProjectMilestoneFilters

    return getProjectMilestones({
      isMore: false,
      params: {
        lead: id,
        omit: OMIT_FINANCIAL_ITEMS,
        ...serializeFilters
      }
    })
  }, [id, filters, getProjectMilestones])

  const pageSize = filters?.page_size || SCROLL_PAGE_SIZE
  const loadedCount = milestones.length
  const fullyLoaded = !nextPageUrl(data)

  // Re-reads every row already on screen in one request and swaps them in
  // place, so a write deep in the list neither drops the pages loaded below
  // page one nor scrolls the table back to the top. A list that held
  // everything asks for a page more, so a row a write just added still lands.
  const refreshLoadedMilestones = useCallback(async () => {
    const serializeFilters = serializeData(
      filters || {}
    ) as ProjectMilestoneFilters

    return getProjectMilestones({
      isMore: false,
      refresh: true,
      params: {
        lead: id,
        omit: OMIT_FINANCIAL_ITEMS,
        ...serializeFilters,
        page: 1,
        page_size: Math.max(
          pageSize,
          fullyLoaded ? loadedCount + pageSize : loadedCount
        )
      }
    })
  }, [id, filters, getProjectMilestones, pageSize, fullyLoaded, loadedCount])

  const handleLoadAllMilestones = useCallback(async () => {
    const serializeFilters = serializeData(
      filters || {}
    ) as ProjectMilestoneFilters

    await getProjectMilestones({
      isMore: false,
      refresh: true,
      params: {
        lead: id,
        omit: OMIT_FINANCIAL_ITEMS,
        ...serializeFilters,
        page: 1,
        page_size: Math.min(count, MILESTONES_LOAD_ALL_CAP)
      }
    })
  }, [id, filters, getProjectMilestones, count])

  // The Gantt and its PDF draw the whole schedule: rows not loaded would be
  // missing, with their arrows, and their successors would show stored dates
  // instead of shifted ones.
  const loadAllTarget = Math.min(count, MILESTONES_LOAD_ALL_CAP)
  const loadAllForGantt = useCallback(async () => {
    if (loadedCount < loadAllTarget) await handleLoadAllMilestones()
  }, [loadedCount, loadAllTarget, handleLoadAllMilestones])

  useEffect(() => {
    if (view === "gantt") void loadAllForGantt()
  }, [view, loadAllForGantt])

  // The API derives some of the milestone's figures from its ledger lines and
  // recomputes them after the write response is serialised, so a write that can
  // move one leaves the row it returned a step behind. Returns the refetched
  // milestones so a caller that needs the just-recomputed figures (e.g. to
  // compare Committed against Planned right after a financial-item write)
  // does not have to read them off a stale render-time closure.
  // A finance-line write sends the milestone's whole list of lines, and the
  // server deletes any line the list leaves out. While a milestone's figures
  // are still being re-read after a write (an auto line may have just been
  // made), its lines wait rather than send a list that may be missing one.
  const [refreshingMilestoneIds, setRefreshingMilestoneIds] = useState<
    string[]
  >([])
  const whileRefreshing = useCallback(
    async (milestoneId: string, work: () => Promise<unknown>) => {
      setRefreshingMilestoneIds((ids) => [...ids, milestoneId])
      try {
        await work()
      } finally {
        setRefreshingMilestoneIds((ids) => {
          const index = ids.indexOf(milestoneId)
          return index < 0
            ? ids
            : [...ids.slice(0, index), ...ids.slice(index + 1)]
        })
      }
    },
    []
  )

  // The list leaves the lines out; an expanded row reads its milestone, and
  // with it the lines, each time it opens. Lines kept from earlier stay held
  // until the fresh read lands, so nothing writes from a stale list.
  const failedLineReads = useRef(new Set<string>())
  const readLines = useCallback(
    (milestoneId: string) =>
      whileRefreshing(milestoneId, () =>
        getProjectMilestoneById(milestoneId).unwrap()
      ).then(
        () => failedLineReads.current.delete(milestoneId),
        (e: unknown) => {
          failedLineReads.current.add(milestoneId)
          console.error("Load finance items error:", e)
        }
      ),
    [getProjectMilestoneById, whileRefreshing]
  )
  const handleExpandMilestone = useCallback(
    (milestoneId: string) => void readLines(milestoneId),
    [readLines]
  )
  // An open row whose lines a list re-read dropped (a new filter or tab) asks
  // for them again — once: a read that failed waits for the row to reopen.
  const handleMissingLines = useCallback(
    (milestoneId: string) => {
      if (failedLineReads.current.has(milestoneId)) return
      void readLines(milestoneId)
    },
    [readLines]
  )

  const refreshMilestonesAndSnapshot = useCallback(async () => {
    const result = await refreshLoadedMilestones()
    handleRefetchFinancialSnapshot()
    return result
  }, [refreshLoadedMilestones, handleRefetchFinancialSnapshot])

  const nextUrl = nextPageUrl(data)
  const handleGetMoreMilestones = useCallback(async () => {
    if (!nextUrl) return

    const serializeFilters =
      extractParamsFromNextUrl<ProjectMilestoneFilters>(nextUrl)

    await getProjectMilestones({
      isMore: true,
      params: { lead: id, ...serializeFilters }
    })
  }, [nextUrl, getProjectMilestones, id])

  const handleChangePage = useCallback(
    (page: number) => setFilters({ page }),
    [setFilters]
  )

  const handleChangeFilter = useCallback(
    (newData: Partial<ProjectMilestoneFilters>) =>
      setFilters({ ...newData, page: 1 }),
    [setFilters]
  )

  const handleGetFilterOptions = useCallback(async () => {
    // todo remove it later pls
    const args = { project_id: id }
    const loadMilestoneOptions = async () => {
      await getFilterOptions(args).unwrap()
    }
    const loadFinanceOptions = async () => {
      if (showFinanceItems) {
        setFinanceOptions(await getFinanceFilterOptions(args).unwrap())
      }
    }

    const results = await Promise.allSettled([
      loadMilestoneOptions(),
      loadFinanceOptions()
    ])
    for (const result of results) {
      if (result.status === "rejected") {
        console.log("Error getting filter options", result.reason)
      }
    }
  }, [getFilterOptions, getFinanceFilterOptions, showFinanceItems, id])

  const handleCreateMilestone = useCallback(
    async (data: ProjectMilestone): Promise<MilestoneSaveResult> => {
      try {
        await createMilestone({
          ...data,
          lead: id,
          order: count + 1,
          financial_items: [],
          project_type: filters?.project_type,
          project_type_assignment: filters?.project_type_assignment
        }).unwrap()
        await refreshMilestonesAndSnapshot()
        return { ok: true }
      } catch (e) {
        console.log("Create milestone error: ", e)
        return { ok: false, fieldErrors: milestoneScheduleFieldErrors(e) }
      }
    },
    [
      refreshMilestonesAndSnapshot,
      createMilestone,
      id,
      count,
      filters?.project_type,
      filters?.project_type_assignment
    ]
  )

  // The single place any milestone date edit goes through — table inline
  // edit today, and the Gantt if it ever gains its own date controls — so
  // the dependency cascade below runs identically regardless of origin.
  const applyMilestoneUpdate = useCallback(
    async (data: Partial<ProjectMilestone> & { id: string }) => {
      await updateMilestone(data).unwrap()
    },
    [updateMilestone]
  )

  // A friendly, non-technical confirmation that the schedule actually
  // changed — shown after any dependency create/update/delete, or an Actual
  // End entry, that moved at least one milestone, so the user isn't left
  // guessing whether their change took effect anywhere beyond the one field
  // they touched.
  const notifyScheduleUpdated = useCallback(
    (
      impact: ReturnType<typeof diffEffectiveSchedule>,
      nameById: Map<string, string>,
      singleMessage: (name: string, date: string) => string,
      pluralMessage: (count: number) => string
    ) => {
      if (!impact.length) return
      const description =
        impact.length === 1
          ? singleMessage(
              nameById.get(impact[0].id) ||
                t("milestoneDependencies.fallbackMilestone"),
              formatDay(impact[0].window.start)
            )
          : pluralMessage(impact.length)
      notification.success({
        message: t("milestoneDependencies.scheduleUpdated"),
        description
      })
    },
    [notification, t, formatDay]
  )

  const notifyGeneratedSchedule = useCallback(
    (
      generatedIds: string[],
      after: EffectiveSchedule,
      nameById: Map<string, string>
    ) => {
      if (!generatedIds.length) return
      const entry =
        generatedIds.length === 1 ? after.get(generatedIds[0]) : undefined
      const description = entry
        ? t("milestoneDependencies.scheduleGenerated.single", {
            name:
              nameById.get(generatedIds[0]) ||
              t("milestoneDependencies.fallbackMilestone"),
            start: formatDay(entry.start),
            end: formatDay(entry.end),
            source:
              (entry.shiftReason &&
                nameById.get(entry.shiftReason.sourceMilestoneId)) ||
              t("milestoneDependencies.fallbackSource")
          })
        : t("milestoneDependencies.scheduleGenerated.many", {
            count: generatedIds.length
          })
      notification.success({
        message: t("milestoneDependencies.scheduleUpdated"),
        description
      })
    },
    [notification, t, formatDay]
  )

  // The server dates an Effort-only target from its dependencies and keeps it
  // following them. A target with neither dates nor an Effort would stay
  // unscheduled there while the Gantt drew it a week long, so it is given
  // that week as its Effort — the span the schedule engine already draws.
  const giveUndatedTargetAnEffort = useCallback(
    async (targetId: string, after: EffectiveSchedule) => {
      const target = milestones.find((m) => m.id === targetId)
      if (!target || !after.get(targetId)?.isGenerated) return []
      if (target.start_date || target.end_date || target.duration_days) {
        return [targetId]
      }
      await updateMilestone({
        id: targetId,
        duration_days: DEFAULT_MILESTONE_DURATION_DAYS,
        silent: true
      }).unwrap()
      return [targetId]
    },
    [milestones, updateMilestone]
  )

  // Writes the shifted window the user confirmed for each chosen dependent —
  // Planned Start/End only, never Actual End. One refused write does not stop
  // the others; the refresh that follows shows what was saved.
  const persistDependentDates = useCallback(
    async (ids: string[], entries: ScheduleImpactEntry[]) => {
      const windowById = new Map(
        entries.map((entry) => [entry.id, entry.window])
      )
      const results = await Promise.allSettled(
        ids.map((dependentId) => {
          const window = windowById.get(dependentId)
          if (!window) return Promise.resolve()
          return updateMilestone({
            id: dependentId,
            start_date: window.start.format(DATE_FORMAT),
            end_date: window.end.format(DATE_FORMAT),
            silent: true
          }).unwrap()
        })
      )
      results.forEach((result) => {
        if (result.status === "rejected") {
          console.log("Save dependent dates error: ", result.reason)
        }
      })
    },
    [updateMilestone]
  )

  const applyDependencyImpact = useCallback(
    async ({
      targetId,
      after,
      shiftedImpact,
      nameById,
      shiftedSingle,
      shiftedMany
    }: {
      targetId: string
      after: EffectiveSchedule
      shiftedImpact: ScheduleImpact
      nameById: Map<string, string>
      shiftedSingle: (name: string, date: string) => string
      shiftedMany: (count: number) => string
    }) => {
      let generatedIds: string[] = []
      try {
        generatedIds = await giveUndatedTargetAnEffort(targetId, after)
      } catch (e) {
        console.log("Set target effort error: ", e)
      }
      await refreshLoadedMilestones()
      notifyScheduleUpdated(shiftedImpact, nameById, shiftedSingle, shiftedMany)
      notifyGeneratedSchedule(generatedIds, after, nameById)
    },
    [
      giveUndatedTargetAnEffort,
      refreshLoadedMilestones,
      notifyScheduleUpdated,
      notifyGeneratedSchedule
    ]
  )

  const handleUpdateMilestone = useCallback(
    async (data: ProjectMilestone): Promise<MilestoneSaveResult> => {
      try {
        // Compare against the EFFECTIVE previous dates, not the raw stored
        // ones — the edit form now opens pre-filled with a shifted
        // milestone's calculated dates (to match what the Table's read row
        // just showed), so if the user saves without touching them, this
        // must read as "unchanged" rather than as a manual edit that would
        // otherwise get persisted as the new baseline, permanently
        // overwriting the milestone's real stored dates with a snapshot of
        // a dependency shift.
        const originalRaw = milestones.find((m) => m.id === editableRow)
        const before = calculateEffectiveSchedule({
          milestones,
          dependencies,
          parseDate
        })
        const previousEntry = before.get(editableRow)
        const previousStart = previousEntry?.start ?? null
        const previousEnd = previousEntry?.end ?? null
        // Only the schedule fields the user changed are written; the server
        // derives the one left out, so the preview does the same from the
        // stored values. A date equal to what the row opened on is an echo —
        // for a shifted milestone that is the calculated date, and writing it
        // would pass a dependency's shift off as a date someone chose.
        const sameDay = (next: Dayjs | null, previous: Dayjs | null) =>
          !next === !previous &&
          (!next || !previous || next.isSame(previous, "day"))
        const isEcho = (field: MilestoneScheduleField) =>
          field === "start_date"
            ? sameDay(parseDate(data.start_date), previousStart)
            : field === "end_date"
              ? sameDay(parseDate(data.end_date), previousEnd)
              : (data.duration_days ?? null) ===
                (originalRaw?.duration_days ?? null)
        const sentSchedule = Object.fromEntries(
          MILESTONE_SCHEDULE_FIELDS.filter(
            (field) => field in data && !isEcho(field)
          ).map((field) => [
            field,
            field === "duration_days"
              ? (data.duration_days ?? null)
              : normalizeDate(data[field])
          ])
        ) as Partial<MilestoneScheduleFields>
        // A row opened on dependency-shifted dates previews a typed date
        // against the other shown date, while the server would derive from
        // the stored one. Sending the shown date beside the typed one stores
        // exactly what the row showed.
        if (
          previousEntry?.isShifted &&
          "start_date" in sentSchedule !== "end_date" in sentSchedule
        ) {
          const shown = resolveMilestoneSchedule(
            {
              start_date: previousStart?.format(DATE_FORMAT) ?? null,
              end_date: previousEnd?.format(DATE_FORMAT) ?? null,
              duration_days: originalRaw?.duration_days ?? null
            },
            sentSchedule
          )
          sentSchedule.start_date = shown.start_date
          sentSchedule.end_date = shown.end_date
        }
        const scheduleEdited = Object.keys(sentSchedule).length > 0
        const resolved = resolveMilestoneSchedule(
          {
            start_date: normalizeDate(originalRaw?.start_date),
            end_date: normalizeDate(originalRaw?.end_date),
            duration_days: originalRaw?.duration_days ?? null
          },
          sentSchedule
        )
        const nextStart = scheduleEdited
          ? parseDate(resolved.start_date)
          : previousStart
        const nextEnd = scheduleEdited
          ? parseDate(resolved.end_date)
          : previousEnd

        // Compared per field, and presence-first, so clearing a date reads as
        // a change rather than as "unchanged" — the same shape `actualEndChanged`
        // below already uses. Requiring both dates to be present treated a
        // cleared Planned End as no edit at all and dropped it from the write.
        const startChanged =
          !!previousStart !== !!nextStart ||
          (!!previousStart &&
            !!nextStart &&
            !nextStart.isSame(previousStart, "day"))
        const endChanged =
          !!previousEnd !== !!nextEnd ||
          (!!previousEnd && !!nextEnd && !nextEnd.isSame(previousEnd, "day"))

        const datesChanged = startChanged || endChanged

        // Actual End has no "effective" concept of its own — it's a raw fact
        // — so it's compared against its own previously stored value, not
        // anything derived.
        const previousActualEnd = originalRaw
          ? parseDate(originalRaw.actual_end_date)
          : null
        const nextActualEnd = parseDate(data.actual_end_date)
        const actualEndChanged =
          !!previousActualEnd !== !!nextActualEnd ||
          (!!previousActualEnd &&
            !!nextActualEnd &&
            !previousActualEnd.isSame(nextActualEnd, "day"))

        let cascade: ReturnType<typeof diffEffectiveSchedule> = []
        let after: EffectiveSchedule | null = null
        let direction: "later" | "earlier" = "later"
        let daysMoved = 0
        // Which of the two kinds of edit is driving the cascade, so the
        // confirmation and the post-save toast can each use wording that
        // actually describes what happened — "completed N days late" reads
        // very differently from "moved N days later".
        const isActualEndChange = actualEndChanged && !datesChanged

        if ((datesChanged || actualEndChanged) && nextStart && nextEnd) {
          // Same engine the Gantt renders from: recompute the whole schedule
          // with this one milestone's dates (and/or actual end) swapped in,
          // and diff it against today's schedule to see exactly what else
          // needs to move. Only this one milestone's own stored dates are
          // ever written below — a dependent's stored dates are never
          // overwritten with a calculated cascade value, so a later move
          // (or a late actual finish) that pushes a dependent out can just
          // as correctly retreat it again if the source is later pulled
          // back earlier (or its actual finish is corrected earlier).
          // Persisting a cascaded value would bake it in as if it were the
          // dependent's own manually chosen date, permanently blocking that
          // retreat.
          after = calculateEffectiveSchedule({
            milestones,
            dependencies,
            parseDate,
            overrides: new Map([
              [
                editableRow,
                {
                  ...(datesChanged ? { start: nextStart, end: nextEnd } : {}),
                  ...(actualEndChanged ? { actualEnd: nextActualEnd } : {})
                }
              ]
            ])
          })
          cascade = diffEffectiveSchedule(before, after, {
            excludeIds: new Set([editableRow])
          })

          if (datesChanged) {
            const startDelta =
              previousStart && !nextStart.isSame(previousStart, "day")
                ? nextStart.diff(previousStart, "day")
                : previousEnd
                  ? nextEnd.diff(previousEnd, "day")
                  : 0
            direction = startDelta < 0 ? "earlier" : "later"
            daysMoved = Math.abs(startDelta)
          } else if (actualEndChanged && nextActualEnd && previousEnd) {
            // "Later/earlier than planned" always compares the actual finish
            // against the PLANNED end — not against whatever actual end (if
            // any) was previously recorded.
            const endDelta = nextActualEnd.diff(previousEnd, "day")
            direction = endDelta < 0 ? "earlier" : "later"
            daysMoved = Math.abs(endDelta)
          }
        }

        const nameById = new Map(milestones.map((m) => [m.id, m.name]))
        const milestoneById = new Map(milestones.map((m) => [m.id, m]))
        const entries: ScheduleImpactEntry[] = cascade.map((entry) => {
          const dependent = milestoneById.get(entry.id)
          return {
            ...entry,
            name:
              nameById.get(entry.id) ||
              t("milestoneScheduleImpact.fallbackName"),
            // The server re-dates a milestone with an Effort and no dates of
            // its own on this very save; one with dates a person chose is
            // saved only when the user ticks it, never silently.
            kind: !after?.get(entry.id)?.isGenerated
              ? "own"
              : dependent?.duration_days
                ? "follows"
                : "undated"
          }
        })
        const choice = defaultScheduleImpactChoice()

        // Handles its own failure: the confirmation below hands it to `onOk`,
        // which antd calls long after the surrounding try/catch returned, and
        // which re-throws a rejection rather than swallowing it.
        const applyAll = async (): Promise<MilestoneSaveResult> => {
          try {
            const {
              start_date: _start,
              end_date: _end,
              duration_days: _effort,
              ...unscheduled
            } = data
            await applyMilestoneUpdate({
              ...unscheduled,
              ...sentSchedule,
              id: editableRow
            })
            // The saved row is already in the store, so the row closes now and
            // the dependents and server-derived figures catch up behind it.
            setEditableRow("")
            const savedId = editableRow
            const linesLoaded =
              milestones.find((m) => m.id === savedId)?.financial_items !==
              undefined
            void (async () => {
              try {
                await whileRefreshing(savedId, async () => {
                  await persistDependentDates(
                    idsToSave(entries, choice),
                    entries
                  )
                  await Promise.all([
                    refreshMilestonesAndSnapshot(),
                    // Its lines are not in the list; a write can move them.
                    linesLoaded
                      ? getProjectMilestoneById(savedId).unwrap()
                      : undefined
                  ])
                })
              } catch (e) {
                console.log("Refresh after milestone update error: ", e)
              }
              // Only the Actual End flow gets the friendly toast here — a
              // plain planned-date edit already has its own explicit "Move &
              // Update" confirmation immediately before this runs, so a second
              // message announcing the same thing again would be redundant.
              if (isActualEndChange) {
                notifyScheduleUpdated(
                  cascade,
                  nameById,
                  (name) =>
                    t("milestoneScheduleImpact.completedToast.single", {
                      name,
                      source: data.name
                    }),
                  (count) =>
                    t("milestoneScheduleImpact.completedToast.many", {
                      source: data.name,
                      span: t("milestoneScheduleImpact.days", {
                        count: daysMoved
                      }),
                      direction: t(
                        `milestoneScheduleImpact.direction.${direction}`
                      ),
                      count
                    })
                )
              }
            })()
            return { ok: true }
          } catch (e) {
            // The write reports itself through `errorSave`; catching here is
            // also what stops the rejection escaping `onOk`, which antd
            // re-throws. A refused schedule field goes back to the row.
            console.log("Update milestone error: ", e)
            return { ok: false, fieldErrors: milestoneScheduleFieldErrors(e) }
          }
        }

        if (!cascade.length) return await applyAll()

        const ledeVars = {
          name: data.name,
          count: cascade.length,
          span: daysMoved
            ? `${t("milestoneScheduleImpact.days", { count: daysMoved })} `
            : "",
          direction: t(`milestoneScheduleImpact.direction.${direction}`)
        }

        return await new Promise<MilestoneSaveResult>((resolve) => {
          modal.confirm({
            title: isActualEndChange
              ? t("milestoneScheduleImpact.titleCompleted")
              : t("milestoneScheduleImpact.titleMoved"),
            width: 520,
            content: (
              <MilestoneScheduleImpactContent
                lede={
                  isActualEndChange
                    ? t("milestoneScheduleImpact.ledeCompleted", ledeVars)
                    : t("milestoneScheduleImpact.ledeMoved", ledeVars)
                }
                entries={entries}
                choice={choice}
                formatDay={formatDay}
              />
            ),
            okText: isActualEndChange
              ? t("milestoneScheduleImpact.okCompleted")
              : t("milestoneScheduleImpact.okMoved"),
            cancelText: t("milestoneScheduleImpact.cancel"),
            onOk: async () => resolve(await applyAll()),
            onCancel: () => resolve({ ok: false })
          })
        })
      } catch (e) {
        console.log("Update milestone error: ", e)
        return { ok: false }
      }
    },
    [
      refreshMilestonesAndSnapshot,
      editableRow,
      applyMilestoneUpdate,
      persistDependentDates,
      whileRefreshing,
      getProjectMilestoneById,
      milestones,
      dependencies,
      parseDate,
      normalizeDate,
      modal,
      notifyScheduleUpdated,
      formatDay,
      t
    ]
  )

  const handleDeleteMilestone = useCallback(
    async (id: string) => {
      modal.confirm({
        title: t("milestoneDelete.title"),
        content: (
          <>
            <p>{t("milestoneDelete.confirm")}</p>
            <p>{t("milestoneDelete.consequence")}</p>
          </>
        ),
        okText: t("milestoneDelete.ok"),
        okType: "danger",
        cancelText: t("milestoneDelete.cancel"),
        onOk: async () => {
          try {
            await removeMilestone(id).unwrap()

            handleRefetchFinancialSnapshot()
          } catch (e) {
            console.log("Delete milestone error: ", e)
          }
        }
      })
    },
    [handleRefetchFinancialSnapshot, removeMilestone, t, modal]
  )

  // Milestones have no bulk endpoint: each action fans the existing per-row
  // call out and comes back with a per-id outcome. The section owns the list
  // refresh (the milestone figures are derived server-side) and drops the
  // selection only once nothing in it was refused.
  //
  // A date shift or a date set is worked out per milestone from its own stored
  // Planned Start/End — never its dependency-shifted display dates, and never
  // Actual End. A milestone the change cannot apply to is refused here, before
  // any request, and reported in the same result.
  const handleBulkAction = useCallback(
    async (params: MilestoneBulkActionParams) => {
      const { action, ids } = params
      let result: MilestoneBulkResult

      if (action === "delete") {
        result = await bulkDeleteMilestones({ ids }).unwrap()
      } else if (action === "change-status") {
        result = await bulkPatchMilestones({
          ids,
          status: params.status as number
        }).unwrap()
      } else {
        const stored = await getMilestonesForBulk({
          ids,
          fallback: t("milestoneBulk.result.unreadable")
        }).unwrap()
        const { patches, refused } = milestoneDatePatches(
          { ...params, ids: stored.found.map((m) => m.id) },
          stored.found,
          parseDate,
          {
            noDatesToShift: t("milestoneBulk.result.noDatesToShift"),
            endBeforeStart: t("milestoneBulk.result.endBeforeStart")
          }
        )
        const sent: MilestoneBulkResult = patches.length
          ? await bulkPatchMilestones({ patches }).unwrap()
          : { requested: 0, succeeded: [], failed: [] }
        result = {
          requested: ids.length,
          succeeded: sent.succeeded,
          failed: [...stored.failed, ...refused, ...sent.failed]
        }
      }

      // Not awaited: the bar reports the outcome as soon as the writes settle.
      // Behind a slow or stuck list read, waiting here left the bar showing
      // nothing at all — not even which milestones were refused.
      void refreshMilestonesAndSnapshot()
      if (!result.failed.length) setSelectedIds([])
      return result
    },
    [
      bulkDeleteMilestones,
      bulkPatchMilestones,
      getMilestonesForBulk,
      refreshMilestonesAndSnapshot,
      parseDate,
      t
    ]
  )

  // Both keys: a tab is its own working set, and a payload naming no instance
  // moves only the type.
  useEffect(() => {
    setSelectedIds([])
  }, [filters?.project_type, filters?.project_type_assignment])

  const onSelectRow = useCallback(
    (record: ProjectMilestone, action: string) => {
      switch (action) {
        case "edit_row":
          return setEditableRow(record?.id)
        case "update":
          return handleUpdateMilestone(record)
        case "cancel":
          return setEditableRow("")
        case "delete_row":
          return handleDeleteMilestone(record?.id)
        case "manage_dependencies":
          return setManageDependenciesFor(record)
      }
    },
    [setEditableRow, handleUpdateMilestone, handleDeleteMilestone]
  )

  const handleCreateDependency = useCallback(
    async (data: MilestoneDependencyWriteData) => {
      const before = calculateEffectiveSchedule({
        milestones,
        dependencies,
        parseDate
      })

      if (
        !before.has(data.source_milestone) &&
        !before.has(data.target_milestone)
      ) {
        notifications.error({
          message: t("milestoneDependencies.datesRequired.title"),
          description: t("milestoneDependencies.datesRequired.description")
        })
        return false
      }

      // Preview what adding this relationship would do to the schedule
      // before it exists — the target milestone (and anything downstream of
      // it) may currently sit at dates the new dependency would forbid.
      const proposedDependency: MilestoneDependency = {
        ...data,
        id: "__pending__",
        type: data.type,
        type_display: "",
        created: "",
        updated: ""
      }
      const after = calculateEffectiveSchedule({
        milestones,
        dependencies: [...dependencies, proposedDependency],
        parseDate
      })
      const impact = diffEffectiveSchedule(before, after)
      const nameById = new Map(milestones.map((m) => [m.id, m.name]))
      const sourceName =
        nameById.get(data.source_milestone) ||
        t("milestoneDependencies.fallbackSourceName")

      // A freshly generated window was never at a "real" date the user could
      // be surprised to see move, so only genuine shifts need confirming.
      const shiftedImpact = shiftedOnly(impact, after)

      const persist = async () => {
        setSavingDependency(true)
        try {
          await createDependency(data).unwrap()
          // The table's Dependencies column reads `dependency_count` off the
          // milestone record, so the refetch inside is not optional here.
          await applyDependencyImpact({
            targetId: data.target_milestone,
            after,
            shiftedImpact,
            nameById,
            shiftedSingle: (name, date) =>
              t("milestoneDependencies.added.single", {
                name,
                date,
                source: sourceName
              }),
            shiftedMany: (count) =>
              t("milestoneDependencies.added.many", { count })
          })
          return true
        } catch (e) {
          console.log("Create dependency error: ", e)
          return false
        } finally {
          setSavingDependency(false)
        }
      }

      if (!shiftedImpact.length) return persist()

      const count = shiftedImpact.length

      return new Promise<boolean>((resolve) => {
        modal.confirm({
          title: t("milestoneDependencies.impact.title"),
          content: (
            <>
              <p>{t("milestoneDependencies.impact.lede", { count })}</p>
              <ul className="list-disc pl-4">
                {shiftedImpact.map(({ id, window, previousWindow }) => (
                  <li key={id}>
                    {nameById.get(id) ||
                      t("milestoneDependencies.impact.fallbackName")}
                    :{" "}
                    {previousWindow && (
                      <>
                        {formatDay(previousWindow.start)} –{" "}
                        {formatDay(previousWindow.end)} →{" "}
                      </>
                    )}
                    {formatDay(window.start)} – {formatDay(window.end)}
                  </li>
                ))}
              </ul>
            </>
          ),
          okText: t("milestoneDependencies.impact.ok"),
          cancelText: t("milestoneDependencies.impact.cancel"),
          onOk: async () => resolve(await persist()),
          onCancel: () => resolve(false)
        })
      })
    },
    [
      createDependency,
      applyDependencyImpact,
      milestones,
      dependencies,
      parseDate,
      modal,
      notifications,
      t,
      formatDay
    ]
  )

  const handleUpdateDependency = useCallback(
    async (id: string, data: Partial<MilestoneDependencyWriteData>) => {
      // Same preview-before-persist shape as create: a lag or relationship
      // type change is just as much a scheduling constraint change as a
      // brand new dependency, so it goes through the same engine.
      const before = calculateEffectiveSchedule({
        milestones,
        dependencies,
        parseDate
      })
      const after = calculateEffectiveSchedule({
        milestones,
        dependencies: dependencies.map((d) =>
          d.id === id ? { ...d, ...data } : d
        ),
        parseDate
      })
      const impact = diffEffectiveSchedule(before, after)
      const nameById = new Map(milestones.map((m) => [m.id, m.name]))
      const shiftedImpact = shiftedOnly(impact, after)
      const targetId =
        data.target_milestone ??
        dependencies.find((d) => d.id === id)?.target_milestone

      setSavingDependency(true)
      try {
        await updateDependency({ ...data, id }).unwrap()
        if (targetId) {
          await applyDependencyImpact({
            targetId,
            after,
            shiftedImpact,
            nameById,
            shiftedSingle: (name, date) =>
              t("milestoneDependencies.updated.single", { name, date }),
            shiftedMany: (count) =>
              t("milestoneDependencies.updated.many", { count })
          })
        }
        return true
      } catch (e) {
        console.log("Update dependency error: ", e)
        return false
      } finally {
        setSavingDependency(false)
      }
    },
    [
      updateDependency,
      dependencies,
      milestones,
      parseDate,
      applyDependencyImpact,
      t
    ]
  )

  const handleDeleteDependency = useCallback(
    async (id: string) => {
      // Preview the effect of the constraint going away before it does —
      // a milestone that was only at its current dates because of this
      // dependency should be free to recalculate once it's gone.
      const before = calculateEffectiveSchedule({
        milestones,
        dependencies,
        parseDate
      })
      const after = calculateEffectiveSchedule({
        milestones,
        dependencies: dependencies.filter((d) => d.id !== id),
        parseDate
      })
      const impact = diffEffectiveSchedule(before, after)
      const nameById = new Map(milestones.map((m) => [m.id, m.name]))

      setSavingDependency(true)
      try {
        await removeDependency(id).unwrap()
        // Same staleness concern as create: the row's own dependency_count
        // only updates on a fresh milestones fetch.
        await refreshLoadedMilestones()
        notifyScheduleUpdated(
          impact,
          nameById,
          (name, date) =>
            t("milestoneDependencies.removed.single", { name, date }),
          (count) => t("milestoneDependencies.removed.many", { count })
        )
        return true
      } catch (e) {
        console.log("Delete dependency error: ", e)
        return false
      } finally {
        setSavingDependency(false)
      }
    },
    [
      removeDependency,
      refreshLoadedMilestones,
      milestones,
      dependencies,
      parseDate,
      notifyScheduleUpdated,
      t
    ]
  )

  // A financial item is a line on the ledger, not the plan — adding or
  // editing one can push Committed past Planned without anyone having
  // touched the milestone's own Planned field. Rather than silently letting
  // the two disagree (or auto-overwriting Planned), offer the user the
  // choice, the same way any other write-vs-plan mismatch in this app asks
  // rather than assumes.
  //
  // Payable only. A milestone's Payable has no auto-generated line — every
  // payable line is manual — so syncing Planned up to Committed converges.
  // Receivable is structurally different: `sync_milestone_receivable_on_save`
  // (ezbuild_back/apps/project_management/signals.py) keeps an auto-generated
  // receivable line mirrored to `planned_receivable`, so that line is always
  // part of `committed_receivable`. The instant any *manual* receivable line
  // also exists, `committed_receivable = planned_receivable + manual_total`
  // holds by construction — raising Planned to match Committed also raises
  // the auto-line (since it re-mirrors the new Planned), so Committed moves
  // right back out of reach by the same `manual_total` on every attempt. No
  // target value converges; offering a "fix" here can only loop. Receivable's
  // disagreement stays a red flag only (MilestoneFinancialCell), the same way
  // a Payable's disagreement was already surfaced with no auto-line and no
  // sync offered, before this feature existed.
  const offerPlannedSync = useCallback(
    (
      updatedMilestone: ProjectMilestone | undefined,
      itemType: FinanceItemTypes | undefined
    ) => {
      if (!updatedMilestone || itemType !== FINANCE_ITEM_TYPES.PAYABLE) return

      const label = t("milestonePlannedSync.payableLabel")
      const committed = Number(updatedMilestone.committed_payable) || 0
      const planned = Number(updatedMilestone.planned_payable) || 0

      if (committed <= planned) return

      modal.confirm({
        title: t("milestonePlannedSync.title"),
        content: t("milestonePlannedSync.content", {
          label,
          planned: budgetFormat(planned),
          committed: budgetFormat(committed)
        }),
        okText: t("milestonePlannedSync.ok"),
        cancelText: t("milestonePlannedSync.cancel"),
        onOk: async () => {
          await updateMilestone({
            id: updatedMilestone.id,
            planned_payable: committed,
            silent: true
          }).unwrap()
          await refreshMilestonesAndSnapshot()
          notification.success({
            message: t("milestonePlannedSync.success", {
              label,
              value: budgetFormat(committed)
            })
          })
        }
      })
    },
    [modal, notification, updateMilestone, refreshMilestonesAndSnapshot, t]
  )

  // A finance-line write closes its row as soon as the write returns; the
  // milestone's recomputed figures, and the offer they may prompt, follow.
  const refreshAndOfferPlannedSync = useCallback(
    async (milestoneId: string, itemType: FinanceItemTypes | undefined) => {
      try {
        let freshMilestone: ProjectMilestone | undefined
        await whileRefreshing(milestoneId, async () => {
          ;[freshMilestone] = await Promise.all([
            getProjectMilestoneById(milestoneId).unwrap(),
            refreshMilestonesAndSnapshot()
          ])
        })
        offerPlannedSync(freshMilestone, itemType)
      } catch (e) {
        console.error("Refresh after finance item write error:", e)
      }
    },
    [
      getProjectMilestoneById,
      refreshMilestonesAndSnapshot,
      offerPlannedSync,
      whileRefreshing
    ]
  )

  const handleDeleteMilestoneFinanceItem = useCallback(
    async (itemId: string, milestone: ProjectMilestone) => {
      // A line write sends the whole list and the server deletes what it
      // leaves out, so it never goes without the lines actually loaded.
      if (!Array.isArray(milestone.financial_items)) {
        console.error("Finance items not loaded for milestone", milestone.id)
        return
      }
      try {
        await updateMilestone({
          id: milestone.id,
          financial_items: milestone.financial_items?.filter(
            (item) => item.id !== itemId
          )
        }).unwrap()
      } catch (e) {
        console.error("Delete finance item error:", e)
        return
      }
      void whileRefreshing(milestone.id, () =>
        Promise.all([
          refreshMilestonesAndSnapshot(),
          getProjectMilestoneById(milestone.id).unwrap()
        ])
      ).catch((e) =>
        console.error("Refresh after finance item delete error:", e)
      )
    },
    [
      refreshMilestonesAndSnapshot,
      updateMilestone,
      whileRefreshing,
      getProjectMilestoneById
    ]
  )

  const handleUpdateMilestoneFinanceItem = useCallback(
    async (
      itemId: string,
      data: Partial<ProjectMilestoneFinanceItem>,
      milestone: ProjectMilestone
    ) => {
      // A line write sends the whole list and the server deletes what it
      // leaves out, so it never goes without the lines actually loaded.
      if (!Array.isArray(milestone.financial_items)) {
        console.error("Finance items not loaded for milestone", milestone.id)
        return
      }
      const existingItem = milestone.financial_items?.find(
        (item) => item.id === itemId
      )
      try {
        await updateMilestone({
          id: milestone.id,
          financial_items: milestone.financial_items?.map((item) =>
            item.id === itemId
              ? { ...item, ...data }
              : {
                  ...item,
                  date: normalizeDate(item?.date),
                  due_date: normalizeDate(item?.due_date)
                }
          )
        }).unwrap()
      } catch (e) {
        console.error("Update finance item error:", e)
        return
      }
      void refreshAndOfferPlannedSync(
        milestone.id,
        data.item_type ?? existingItem?.item_type
      )
    },
    [refreshAndOfferPlannedSync, updateMilestone, normalizeDate]
  )

  const handleAddMilestoneFinanceItem = useCallback(
    async (
      data: Partial<ProjectMilestoneFinanceItem>,
      milestone: ProjectMilestone
    ) => {
      // A line write sends the whole list and the server deletes what it
      // leaves out, so it never goes without the lines actually loaded.
      if (!Array.isArray(milestone.financial_items)) {
        console.error("Finance items not loaded for milestone", milestone.id)
        return false
      }
      try {
        await updateMilestone({
          id: milestone.id,
          financial_items: [
            ...(milestone.financial_items?.map((el) => ({
              ...el,
              date: normalizeDate(el?.date),
              due_date: normalizeDate(el?.due_date)
            })) || []),
            { ...data, project_type: filters?.project_type }
          ] as ProjectMilestone["financial_items"]
        }).unwrap()
      } catch (e) {
        console.error("Add finance item error:", e)
        return false
      }
      void refreshAndOfferPlannedSync(milestone.id, data.item_type)
      return true
    },
    [
      refreshAndOfferPlannedSync,
      updateMilestone,
      filters?.project_type,
      normalizeDate
    ]
  )

  const handleAddProjectType = useCallback(
    async (data: ProjectTypeFormData) => {
      let details: ProjectTypeDetail[] | undefined

      try {
        details = await addProjectType({ projectId: id, data }).unwrap()
      } catch (error) {
        if (!isRepeatedTypeRefusal(error)) throw error
        // Declining is not a finished add: closing drops what was just typed.
        if (!(await confirmRepeatedType(modal, t))) return false

        details = await addProjectType({
          projectId: id,
          data: { ...data, allow_duplicate: true }
        }).unwrap()
      }

      if (data.category !== PROJECT_ENTITY_CATEGORIES.CHANGE_REQUEST) return

      // Every instance of a type shares its `id`, so a plain find gets the first.
      const created = newestInstanceOf(details, data.project_type_id)
      if (created) setPendingDecision(created)
    },
    [addProjectType, modal, t, id]
  )

  const handleUpdateProjectType = useCallback(
    async (data: ProjectTypeFormData, previous: ProjectTypeDetail) => {
      const prompt = projectTypeContractPrompt(previous, data.category)

      if (prompt === "reverse-contract") {
        setPendingReversal({ previous, next: data })
        return
      }

      const details = await updateProjectType({ projectId: id, data }).unwrap()
      await refreshMilestonesAndSnapshot()

      if (prompt !== "contract-decision") return

      const updated = details?.find((detail: ProjectTypeDetail) =>
        data.assignment_id
          ? detail.assignment_id === data.assignment_id
          : detail.id === data.project_type_id
      )
      if (updated) setPendingDecision(updated)
    },
    [updateProjectType, refreshMilestonesAndSnapshot, id]
  )

  const handleContractDecision = useCallback(
    async (decision: ContractDecision) => {
      if (!pendingDecision) return
      await recordContractDecision({
        projectId: id,
        data: {
          project_type_id: pendingDecision.id,
          assignment_id: pendingDecision.assignment_id,
          decision
        }
      }).unwrap()
      setPendingDecision(null)
      await refreshMilestonesAndSnapshot()
    },
    [recordContractDecision, refreshMilestonesAndSnapshot, pendingDecision, id]
  )

  const handleReorderMilestones = useCallback(
    (orderedIds: string[]) => {
      void reorderMilestones(orderedIds.map((id, order) => ({ id, order })))
    },
    [reorderMilestones]
  )

  const handleReorderProjectTypes = useCallback(
    async (orderedKeys: string[]) => {
      const details = project?.project_type_details ?? []
      const items = orderedKeys
        .map((key, order) => {
          const detail = details.find((item) => instanceKeyOf(item) === key)
          return detail
            ? {
                project_type_id: detail.id,
                assignment_id: detail.assignment_id,
                order
              }
            : null
        })
        .filter((item) => item !== null)

      await reorderProjectTypes({ projectId: id, items }).unwrap()
    },
    [reorderProjectTypes, project?.project_type_details, id]
  )

  // By type when the payload names no instance: a backend deployed before this
  // one sends no assignment id, and a bare assignment id matches no tab.
  const activeTabKey = useMemo(() => {
    const details = project?.project_type_details ?? []
    const open = details.find((detail) =>
      filters?.project_type_assignment
        ? detail.assignment_id === filters.project_type_assignment
        : detail.id === filters?.project_type
    )
    return open ? instanceKeyOf(open) : ""
  }, [
    project?.project_type_details,
    filters?.project_type_assignment,
    filters?.project_type
  ])

  const handleDeleteProjectType = useCallback(
    async (detail: ProjectTypeDetail) => {
      if (detail && deleteNeedsContractPrompt(detail)) {
        setPendingDeletion(detail)
        return
      }

      modal.confirm({
        title: t("projectTypeRemove.title"),
        content: (
          <>
            <p>{t("projectTypeRemove.lede", { count: 1 })}</p>
            <p>{t("projectTypeRemove.consequence")}</p>
          </>
        ),
        okText: t("projectTypeRemove.confirm"),
        okType: "danger",
        cancelText: t("projectTypeRemove.cancel"),
        onOk: async () => {
          await removeProjectType({
            projectId: id,
            project_type_id: detail.id,
            assignment_id: detail.assignment_id
          }).unwrap()
          await refreshMilestonesAndSnapshot()
        }
      })
    },
    [modal, t, removeProjectType, refreshMilestonesAndSnapshot, id]
  )

  const handleSaveProjectTypeAsTemplate = useCallback(
    (detail: ProjectTypeDetail) => {
      setSavingAsTemplate(detail)
    },
    []
  )

  const handleSubmitSaveAsTemplate = useCallback(
    async (name: string) => {
      if (!savingAsTemplate) return
      await saveProjectTypeAsTemplate({
        projectId: id,
        project_type_id: savingAsTemplate.id,
        assignment_id: savingAsTemplate.assignment_id,
        name
      }).unwrap()
    },
    [saveProjectTypeAsTemplate, savingAsTemplate, id]
  )

  const handleConfirmedDeletion = useCallback(
    async (reverse_contract: boolean) => {
      if (!pendingDeletion) return
      await removeProjectType({
        projectId: id,
        project_type_id: pendingDeletion.id,
        assignment_id: pendingDeletion.assignment_id,
        reverse_contract
      }).unwrap()
      setPendingDeletion(null)
      await refreshMilestonesAndSnapshot()
    },
    [removeProjectType, refreshMilestonesAndSnapshot, pendingDeletion, id]
  )

  const handleConfirmedReversal = useCallback(
    async (reverse_contract: boolean) => {
      if (!pendingReversal) return
      const { next } = pendingReversal

      await updateProjectType({
        projectId: id,
        data: { ...next, reverse_contract }
      }).unwrap()
      setPendingReversal(null)
      await refreshMilestonesAndSnapshot()
    },
    [updateProjectType, refreshMilestonesAndSnapshot, pendingReversal, id]
  )

  useEffect(() => {
    void handleGetMilestones()
  }, [id, handleGetMilestones])

  useEffect(() => {
    if (id) void getMilestoneDependencies(id)
  }, [id, getMilestoneDependencies])

  useEffect(() => {
    void handleGetFilterOptions()
  }, [handleGetFilterOptions])

  // `overflow-clip`, not `auto` or `hidden`: both of those make this card a
  // scroll container, and the add control's `position: sticky` then resolves
  // against a box that never scrolls — so it rode down with the table instead
  // of holding above the fold. `clip` still clips the flush table to the
  // card's radius but is not a scrollport, so the action sticks to
  // `FixedContainer`, which is what actually scrolls here.
  return (
    <Card
      className="overflow-clip"
      classNames={{ body: "p-0", header: "p-2" }}
      data-guide-target="project-milestones-panel"
      title={
        <ProjectViewMilestonesHeader
          project={project}
          isLocked={!!editableRow}
          activeTabKey={activeTabKey}
          //
          projectEditAllowed={canEditProject(projectPermissions, project)}
          editAllowed={projectPermissions?.milestones?.project_milestone_edit}
          saveAsTemplateAllowed={
            saveAsTemplateAllowed && !isArchivedProject(project)
          }
          //
          view={view}
          onViewChange={setView}
          //
          onFilterChange={handleChangeFilter}
          onAddProjectType={handleAddProjectType}
          onDeleteProjectType={handleDeleteProjectType}
          onReorderProjectTypes={handleReorderProjectTypes}
          onUpdateProjectType={handleUpdateProjectType}
          onSaveProjectTypeAsTemplate={handleSaveProjectTypeAsTemplate}
        />
      }
    >
      {pendingDecision && (
        <ContractDecisionModal
          open
          projectTypeName={typeLabel(pendingDecision)}
          estimatedReceivable={Number(
            pendingDecision.estimated_receivable ?? 0
          )}
          estimatedPayable={Number(pendingDecision.estimated_payable ?? 0)}
          canUpdateContract={
            !!projectPermissions?.finances?.project_finance_edit
          }
          onDecide={handleContractDecision}
        />
      )}

      {pendingDeletion && (
        <DeleteProjectTypeModal
          open
          isIncludedInContract
          onCancel={() => setPendingDeletion(null)}
          onDelete={handleConfirmedDeletion}
        />
      )}

      {pendingReversal && (
        <DeleteProjectTypeModal
          open
          isIncludedInContract
          onCancel={() => setPendingReversal(null)}
          onDelete={handleConfirmedReversal}
        />
      )}

      {savingAsTemplate && (
        <SaveProjectTypeAsTemplateModal
          open
          defaultName={`${typeLabel(savingAsTemplate)} (Template)`}
          onClose={() => setSavingAsTemplate(null)}
          onSubmit={handleSubmitSaveAsTemplate}
        />
      )}

      {showAddMilestone && (
        <ProjectMilestoneModal
          open
          options={filterOptions}
          onClose={() => setShowAddMilestone(false)}
          onSubmit={handleCreateMilestone}
        />
      )}

      {view === "table" ? (
        <ProjectViewMilestonesList
          refreshingMilestoneIds={refreshingMilestoneIds}
          onExpandMilestone={handleExpandMilestone}
          onMissingLines={handleMissingLines}
          showFinanceItems={showFinanceItems}
          userId={userId}
          projectPermissions={projectPermissions}
          projectId={id}
          milestones={milestones}
          dependencies={dependencies}
          contractValue={project?.sales_price ?? null}
          contractExpenses={project?.planned_payable ?? null}
          options={filterOptions}
          financeOptions={financeOptions}
          next={data?.next}
          retryNext={data?.retryNext}
          count={count}
          loadAllCap={MILESTONES_LOAD_ALL_CAP}
          onLoadAll={handleLoadAllMilestones}
          editableRow={editableRow}
          fetching={fetching}
          createAllowed={
            projectPermissions?.milestones?.project_milestone_create
          }
          createDisabled={!filters?.project_type || !!editableRow}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
          onBulkAction={handleBulkAction}
          onAddMilestone={() => setShowAddMilestone(true)}
          onLoadMore={handleGetMoreMilestones}
          onSelectRow={onSelectRow}
          onPageChange={handleChangePage}
          onDeleteMilestoneFinanceItem={handleDeleteMilestoneFinanceItem}
          onUpdateMilestoneFinanceItem={handleUpdateMilestoneFinanceItem}
          onAddMilestoneFinanceItem={handleAddMilestoneFinanceItem}
          onReorderMilestones={handleReorderMilestones}
        />
      ) : (
        <div className="p-3">
          <ProjectViewMilestonesGantt
            milestones={milestones}
            dependencies={dependencies}
            projectName={project?.lead_name}
            projectId={id}
            onBeforePdfExport={loadAllForGantt}
          />
        </div>
      )}

      {manageDependenciesFor && (
        <MilestoneDependenciesModal
          milestone={manageDependenciesFor}
          milestones={milestones}
          dependencies={dependencies}
          saving={savingDependency}
          onClose={() => setManageDependenciesFor(null)}
          onCreate={handleCreateDependency}
          onUpdate={handleUpdateDependency}
          onDelete={handleDeleteDependency}
          onMarkPending={() =>
            applyMilestoneUpdate({
              id: manageDependenciesFor.id,
              status: MILESTONE_STATUSES.PENDING
            })
          }
        />
      )}
    </Card>
  )
}

const mapStateToProps = (state: RootState) => ({
  userId: state.auth.userInfo.id,
  projectPermissions: selectProjectWritePermissions(state),
  saveAsTemplateAllowed:
    !!state.auth.permissions?.codes?.settings?.project_templates
      ?.settings_template_create,
  pages: state.auth.permissions.pages,
  filters: state.project.milestonesFilters,
  filterOptions: state.project.milestonesFilterOptions,
  fetching: state.project.fetchingSection,
  data: state.project.milestones,
  dependencies: state.project.milestoneDependencies
})

const mapDispatchToProps = {
  createMilestone: createProjectMilestone,
  updateMilestone: patchProjectMilestone,
  removeMilestone: deleteProjectMilestone,
  reorderMilestones: reorderProjectMilestones,
  bulkPatchMilestones: bulkPatchProjectMilestones,
  bulkDeleteMilestones: bulkDeleteProjectMilestones,
  getMilestonesForBulk: fetchProjectMilestonesForBulk,
  getProjectMilestones: fetchProjectMilestones,
  getProjectMilestoneById: fetchProjectMilestoneById,
  getFilterOptions: fetchProjectMilestonesFilterOptions,
  getFinanceFilterOptions: fetchFinancialTransactionItemsFilterOptions,
  getMilestoneDependencies: fetchProjectMilestoneDependencies,
  createDependency: createMilestoneDependency,
  updateDependency: patchMilestoneDependency,
  removeDependency: deleteMilestoneDependency,
  addProjectType: addProjectTypeToProject,
  updateProjectType: updateProjectTypeToProject,
  recordContractDecision: recordProjectTypeContractDecision,
  removeProjectType: removeProjectTypeToProject,
  saveProjectTypeAsTemplate,
  reorderProjectTypes,
  setProp: projectSetProp,
  refetchFinancialSnapshot: fetchFinancialSnapshot,
  getFinancialAlerts: fetchFinancialAlerts
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewMilestonesSection)
